import { Icons } from "assets";
import classNames from "classnames";

import { useEffect, useId, useRef, useState } from "react";
import { getLeftPosition, getTopPosition } from "shared/utils/helper";
import OptionsDropDown from "../options";
import styles from "./style.module.scss";

interface InputProps extends React.HTMLProps<HTMLInputElement> {
  label?: string;
  error?: string;
  warning?: string;
  notRequiredMargin?: boolean;
  customLabelStyle?: any;
  customInputContainer?: any;
  customOptionsContainer?: any;
  customIconStyle?: any;
  readOnlyColor?: string;
  options: OptionProps[];
  direction?: "top" | "bottom";
  wrapInGlobalWrapper?: boolean;
  selectedOption?: OptionProps;
  clearSelection?: () => void;
}

const CustomSelect = ({
  label,
  required,
  error,
  warning,
  value,
  placeholder,
  notRequiredMargin,
  customLabelStyle,
  customInputContainer,
  customOptionsContainer,
  customIconStyle,
  readOnly,
  readOnlyColor,
  options,
  tabIndex,
  direction = "top",
  wrapInGlobalWrapper = true,
  selectedOption,
  clearSelection,
}: InputProps) => {
  const openProfileDropDownRef = useRef<boolean>(false);
  const [openProfileDropDown, setOpenDropDown] = useState<boolean>(false);
  const uid = useId();

  useEffect(() => {
    if (openProfileDropDown && wrapInGlobalWrapper) {
      const elem = document.getElementById(
        `custom-select-container-${uid}`
      ) as HTMLDivElement;
      const optionsDropDown: HTMLDivElement | null = document.getElementById(
        `custom-select-dropdown-${uid}`
      ) as HTMLDivElement;
      if (elem && optionsDropDown) {
        const topPosition = getTopPosition(elem);
        const leftPosition = getLeftPosition(elem);
        if (direction === "top") {
          optionsDropDown.style.top =
            Number(topPosition + elem?.clientHeight) + "px";
        } else {
          optionsDropDown.style.top =
            Number(topPosition - optionsDropDown?.clientHeight + 20) + "px";
        }

        optionsDropDown.style.left = leftPosition + "px";
        optionsDropDown.style.width = elem?.clientWidth + "px";
      }
    }

    // eslint-disable-next-line
  }, [openProfileDropDown]);

  useEffect(() => {
    if (openProfileDropDown) {
      const observer = new window.IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting && openProfileDropDownRef.current) {
            const elem = document.getElementById(
              `custom-select-container-${uid}`
            ) as HTMLDivElement;
            const optionsDropDown: HTMLDivElement | null =
              document.getElementById(
                `custom-select-dropdown-${uid}`
              ) as HTMLDivElement;
            if (elem && optionsDropDown) {
              const topPosition = getTopPosition(elem);
              const leftPosition = getLeftPosition(elem);

              optionsDropDown.style.top =
                Number(topPosition - optionsDropDown?.clientHeight + 20) + "px";

              optionsDropDown.style.left = leftPosition + "px";
              optionsDropDown.style.width = elem?.clientWidth + "px";
            }
            return;
          }
        },
        {
          root: null,
          threshold: 0.95, // set offset 0.1 means trigger if atleast 10% of element in viewport
        }
      );
      const optionsDropDown: HTMLDivElement | null = document.getElementById(
        `custom-select-dropdown-${uid}`
      ) as HTMLDivElement;

      observer.observe(optionsDropDown);
    }
    // eslint-disable-next-line
  }, [openProfileDropDown]);

  return (
    <div
      className={classNames(
        "relative w-full",
        notRequiredMargin ? "" : error ? "mb-0" : "mb-3"
      )}
      id={`custom-select-container-${uid}`}
    >
      <div className="flex flex-col items-start w-full gap-2">
        {label ? (
          <label
            className={classNames(
              styles.inputLabel,
              customLabelStyle && customLabelStyle
            )}
          >
            {label} {!!required && <label className={styles.asterik}>*</label>}
          </label>
        ) : null}

        <div
          className={classNames(
            styles.inputContainer,
            readOnly ? styles.readonly : "",
            customInputContainer && customInputContainer,
            "w-full gap-2 cursor-pointer "
          )}
          style={readOnlyColor ? { background: readOnlyColor } : {}}
          onClick={() => {
            if (!readOnly) {
              setOpenDropDown(true);
              openProfileDropDownRef.current = true;
            }
          }}
        >
          <span
            className={classNames(value ? styles.value : styles.placeholder)}
          >
            {value ? value : placeholder}
          </span>
          <Icons.ChevDown
            className={classNames(customIconStyle, styles.iconStyle)}
          />
        </div>
        <OptionsDropDown
          id={`custom-select-dropdown-${uid}`}
          options={options}
          openSelection={openProfileDropDown}
          setOpenSelection={(val) => {
            setOpenDropDown(val);
            openProfileDropDownRef.current = val;
          }}
          customContainer={classNames(
            styles.optionsContainer,
            customOptionsContainer
          )}
          tabIndex={tabIndex}
          wrapInGlobalWrapper={wrapInGlobalWrapper}
          selectedOption={selectedOption}
          clearSelection={clearSelection}
        />
      </div>
      {!!error && <div className={classNames(styles.error)}>{error}</div>}
      {!!warning && <div className={classNames(styles.warning)}>{warning}</div>}
    </div>
  );
};

export default CustomSelect;
